import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getProducts, getArticles, getSettings, getImageUrl } from "@/lib/api";
import { ArrowRight, BookOpen, Settings } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import GradientBackground from "@/components/GradientBackground";
import Footer from "@/components/Footer";

function normalizePreviewText(text) {
  return (text || "").replace(/\*/g, "").replace(/\s+/g, " ").trim();
}

function getProductCardDescription(product) {
  const sections = Array.isArray(product.landing_sections) ? product.landing_sections : [];
  const introSection = sections.find((section) => section?.type === "intro");
  const heroSection = sections.find((section) => section?.type === "hero");

  return normalizePreviewText(
    introSection?.title ||
    heroSection?.title ||
    product.description ||
    introSection?.description ||
    heroSection?.description ||
    ""
  );
}

export default function PublicHome() {
  const [products, setProducts] = useState([]);
  const [articles, setArticles] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getProducts(true),
      getArticles(true),
      getSettings(),
    ]).then(([prods, arts, sett]) => {
      setProducts(prods);
      setArticles(arts.slice(0, 3));
      setSettings(sett);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]" data-testid="loading-spinner">
        <div className="w-8 h-8 border-2 border-pink-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white relative overflow-x-hidden" data-testid="public-home">
      {/* Animated GradFlow Background */}
      <GradientBackground />

      {/* Nav */}
      <nav className="sticky top-0 z-40 bg-black/70 backdrop-blur-xl border-b border-[#ff0090]/30" data-testid="public-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 h-14 sm:h-16 flex items-center justify-between">
          <Link to="/" className="font-['Outfit'] font-bold text-lg sm:text-xl text-white tracking-tight" data-testid="nav-logo">
            {settings.site_name || "LinkStore"}
          </Link>
          <div className="flex items-center gap-3 sm:gap-6">
            {articles.length > 0 && (
              <Link to="/blog" className="text-xs sm:text-sm font-medium text-white/70 hover:text-white transition-colors flex items-center gap-1 sm:gap-1.5" data-testid="nav-blog-link">
                <BookOpen className="w-4 h-4" />
                <span className="hidden xs:inline">Blog</span>
              </Link>
            )}
            <Link to="/admin" className="text-xs sm:text-sm font-medium text-white/70 hover:text-white transition-colors flex items-center gap-1 sm:gap-1.5" data-testid="nav-admin-link">
              <Settings className="w-4 h-4" />
              <span className="hidden xs:inline">Admin</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative isolate py-12 sm:py-20 md:py-32 px-4 sm:px-6 md:px-12" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            {settings.avatar_url && (
              <img
                src={getImageUrl(settings.avatar_url)}
                alt="Avatar"
                className="w-20 h-20 sm:w-28 sm:h-28 rounded-full object-cover mb-4 sm:mb-6 shadow-lg shadow-pink-500/40 mx-auto ring-4 ring-[#ff0090]/60"
                data-testid="hero-avatar"
              />
            )}
            <h1 className="font-['Outfit'] text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tight text-balance text-white leading-none" data-testid="hero-title">
              {settings.site_name || "LinkStore"}
            </h1>
            {settings.site_description && (
              <p className="mt-4 sm:mt-6 md:mt-8 text-sm sm:text-base md:text-lg lg:text-xl font-medium text-pretty text-white/70 leading-relaxed whitespace-pre-line px-2" data-testid="hero-description">
                {settings.site_description}
              </p>
            )}
            {/* Social Media Links */}
            {settings.social_links && Object.keys(settings.social_links).length > 0 && (
              <div className="mt-5 sm:mt-6 flex items-center justify-center gap-3 sm:gap-4" data-testid="social-links">
                {settings.social_links.instagram && (
                  <a href={settings.social_links.instagram} target="_blank" rel="noopener noreferrer" className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-black/70 backdrop-blur-md border-2 border-[#ff0090] flex items-center justify-center text-[#ff0090] hover:text-white hover:bg-[#ff0090] transition-all hover:scale-110 shadow-lg shadow-[#ff0090]/40" data-testid="social-instagram">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                  </a>
                )}
                {settings.social_links.tiktok && (
                  <a href={settings.social_links.tiktok} target="_blank" rel="noopener noreferrer" className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-black/70 backdrop-blur-md border-2 border-[#ff0090] flex items-center justify-center text-[#ff0090] hover:text-white hover:bg-[#ff0090] transition-all hover:scale-110 shadow-lg shadow-[#ff0090]/40" data-testid="social-tiktok">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 0010.86 4.48v-7.15a8.16 8.16 0 005.58 2.2V11.3a4.85 4.85 0 01-2.68-.81V6.69h2.68z"/></svg>
                  </a>
                )}
                {settings.social_links.youtube && (
                  <a href={settings.social_links.youtube} target="_blank" rel="noopener noreferrer" className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-black/70 backdrop-blur-md border-2 border-[#ff0090] flex items-center justify-center text-[#ff0090] hover:text-white hover:bg-[#ff0090] transition-all hover:scale-110 shadow-lg shadow-[#ff0090]/40" data-testid="social-youtube">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Products Bento Grid */}
      {products.length > 0 && (
        <section className="relative px-4 sm:px-6 md:px-12 pb-16 sm:pb-20" data-testid="products-section">
          <div className="max-w-5xl mx-auto">
            <div className="bento-grid" data-testid="products-grid">
              {products.map((product, idx) => (
                <Link
                  key={product.id}
                  to={`/product/${product.id}`}
                  className="product-card rounded-2xl bg-black/50 backdrop-blur-md border-2 border-[#ff0090]/40 overflow-hidden animate-fade-in-up hover:bg-black/70 hover:border-[#ff0090] hover:shadow-xl hover:shadow-[#ff0090]/20 transition-all duration-300 flex flex-col"
                  style={{ animationDelay: `${idx * 80}ms`, opacity: 0 }}
                  data-testid={`product-card-${product.id}`}
                >
                  {product.image_url && (
                    <div className="relative overflow-hidden aspect-[4/3]">
                      <img
                        src={getImageUrl(product.image_url)}
                        alt={product.title}
                        className="w-full h-full object-contain"
                        data-testid={`product-card-image-${product.id}`}
                      />
                    </div>
                  )}
                  <div className="p-3 sm:p-4 flex flex-col justify-between flex-grow">
                    <div>
                      <h3 className="font-['Outfit'] font-bold text-white text-sm sm:text-base leading-tight line-clamp-1" data-testid={`product-card-title-${product.id}`}>{product.title}</h3>
                      {getProductCardDescription(product) && (
                        <p className="text-xs text-white/60 mt-1 line-clamp-2" data-testid={`product-card-description-${product.id}`}>{getProductCardDescription(product)}</p>
                      )}
                    </div>
                    <div className="flex items-center text-[#ff0090] text-xs font-medium mt-2" data-testid={`product-card-cta-${product.id}`}>
                      Ansehen <ArrowRight className="w-3 h-3 ml-1" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Blog Preview */}
      {articles.length > 0 && (
        <section className="relative px-4 sm:px-6 md:px-12 pb-24" data-testid="blog-preview-section">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-['Outfit'] text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-white">Blog</h2>
              <Link to="/blog" className="text-sm font-medium text-[#ff0090] hover:text-[#ff69b4] transition-colors flex items-center gap-1" data-testid="view-all-blog-link">
                Alle Artikel <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
              {articles.map((article, idx) => (
                <Link
                  key={article.id}
                  to={`/blog/${article.id}`}
                  className="group rounded-2xl bg-black/50 backdrop-blur-md border-2 border-[#ff0090]/40 overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:bg-black/70 hover:border-[#ff0090] hover:shadow-xl hover:shadow-[#ff0090]/20 animate-fade-in-up"
                  style={{ animationDelay: `${idx * 100}ms`, opacity: 0 }}
                  data-testid={`blog-card-${article.id}`}
                >
                  {article.cover_image_url && (
                    <div className="overflow-hidden h-40 sm:h-44">
                      <img
                        src={getImageUrl(article.cover_image_url)}
                        alt={article.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                  )}
                  <div className="p-4 sm:p-5">
                    {article.tags?.length > 0 && (
                      <div className="flex gap-2 mb-2">
                        {article.tags.slice(0, 2).map(tag => (
                          <Badge key={tag} variant="secondary" className="text-xs bg-[#ff0090]/30 text-[#ff69b4] border border-[#ff0090]/50">{tag}</Badge>
                        ))}
                      </div>
                    )}
                    <h3 className="font-['Outfit'] font-bold text-white text-sm sm:text-base leading-tight">{article.title}</h3>
                    <p className="text-xs sm:text-sm text-white/60 mt-2 line-clamp-2">{article.content}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Empty state */}
      {products.length === 0 && articles.length === 0 && (
        <section className="px-6 md:px-12 pb-20" data-testid="empty-state">
          <div className="max-w-7xl mx-auto text-center py-20">
            <div className="w-16 h-16 rounded-2xl bg-white/[0.05] flex items-center justify-center mx-auto mb-4">
              <Settings className="w-8 h-8 text-white/30" />
            </div>
            <h2 className="font-['Outfit'] text-2xl font-bold text-white mb-2">Noch keine Inhalte</h2>
            <p className="text-white/40 mb-6">Gehe zum Admin-Bereich, um Produkte und Artikel hinzuzufügen.</p>
            <Link to="/admin" className="btn-gradient inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium" data-testid="go-to-admin-btn">
              Zum Admin <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
}
